import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { sportingService } from '../../services/sportingService';
import { LoadingSpinner } from '../../components/UI/LoadingSpinner';
import type { PlayerIntelligenceDashboard as DashboardType } from '../../types/player';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  DollarSign,
  Activity,
  Heart,
  Calendar,
  BarChart3,
  Shield,
  Target,
  Trophy,
  Users
} from 'lucide-react';

const PlayerIntelligenceDashboard: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { playerId } = useParams<{ playerId: string }>();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState<DashboardType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (playerId) {
      loadDashboard();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerId]);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await sportingService.getPlayerIntelligenceDashboard(playerId!);
      setDashboard(data);
    } catch (error: any) {
      console.error('Error loading dashboard:', error);
      setError(error?.message || t('playerDetail.errorLoading'));
      toast.error(t('playerDetail.errorLoadingIntelligence'));
    } finally {
      setLoading(false);
    }
  };

  const getRecommendationColor = (recommendation?: string) => {
    switch (recommendation) {
      case 'green':
        return 'bg-green-500';
      case 'yellow':
        return 'bg-yellow-500';
      case 'red':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getRecommendationIcon = (recommendation?: string) => {
    switch (recommendation) {
      case 'green':
        return <CheckCircle className="w-8 h-8 text-green-500" />;
      case 'yellow':
        return <AlertTriangle className="w-8 h-8 text-yellow-500" />;
      case 'red':
        return <AlertTriangle className="w-8 h-8 text-red-500" />;
      default:
        return <Activity className="w-8 h-8 text-gray-500" />;
    }
  };

  const formatCurrency = (amount?: number | null) => {
    if (amount === null || amount === undefined || Number.isNaN(amount)) return t('playerDetail.notAvailable');
    const locale = i18n?.language || 'es-ES';
    try {
      return new Intl.NumberFormat(locale === 'en' ? 'en-GB' : locale, {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 0
      }).format(amount);
    } catch {
      return amount.toString();
    }
  };

  // Defensive: if loading still true show spinner
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  // Defensive: if error or missing dashboard
  if (error || !dashboard) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <div className="text-center text-white">
          <AlertTriangle className="w-20 h-20 mx-auto mb-4 text-red-400" />
          <h2 className="text-2xl font-bold mb-2">{t('playerDetail.error')}</h2>
          <p className="text-gray-300 mb-6">{error || t('playerDetail.couldNotLoad')}</p>
          <button
            onClick={() => navigate(-1)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
          >
            {t('playerDetail.back')}
          </button>
        </div>
      </div>
    );
  }

  const playerName =
    dashboard?.player_basic?.player_name ||
    dashboard?.player_basic?.nombre_transfermarkt ||
    dashboard?.player_basic?.nombre_fbref ||
    t('playerDetail.player');

  // Safe accessors for nested properties (avoid runtime undefined)
  const playerScore = dashboard?.player_score ?? ({} as any);
  const roiAnalysis = dashboard?.roi_analysis ?? ({} as any);
  const contractSituation = dashboard?.contract_situation ?? ({} as any);
  const marketValuePrediction = dashboard?.market_value_prediction ?? ({} as any);
  const injuryRisk = dashboard?.injury_risk_assessment ?? ({} as any);
  const performances = Array.isArray(dashboard?.performances) ? dashboard.performances : [];
  const transferHistory = Array.isArray(dashboard?.transfer_history) ? dashboard.transfer_history : [];
  const nationalTeam = Array.isArray(dashboard?.national_team) ? dashboard.national_team : [];
  const injuryHistory = Array.isArray(dashboard?.injury_history) ? dashboard.injury_history : [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Header */}
      <div className="bg-black/20 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button onClick={() => navigate(-1)} className="text-white hover:text-blue-400 transition-colors">
                <ArrowLeft className="w-6 h-6" />
              </button>
              <h1 className="text-2xl font-bold text-white">{t('playerDetail.intelligenceDashboard')}</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Player Header with Photo */}
        <div className="bg-gradient-to-r from-blue-800/30 to-blue-900/30 backdrop-blur-sm rounded-3xl p-6 mb-8 border border-white/10">
          <div className="flex items-center space-x-6">
            <div className="flex-shrink-0">
              <div className="w-24 h-24 bg-gradient-to-br from-blue-600 to-blue-800 rounded-full flex items-center justify-center shadow-2xl ring-4 ring-white/20">
                {dashboard?.player_basic?.player_image_url || dashboard?.player_basic?.url_foto_jugador ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={dashboard.player_basic.player_image_url || dashboard.player_basic.url_foto_jugador}
                    alt={playerName}
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  <Target className="w-12 h-12 text-white" />
                )}
              </div>
            </div>
            <div>
              <h2 className="text-3xl font-bold text-white mb-2">{playerName}</h2>
              <div className="flex items-center space-x-4 text-gray-300">
                <span>{dashboard?.player_basic?.position || dashboard?.player_basic?.posicion_transfermarkt || '-'}</span>
                <span>•</span>
                <span>{dashboard?.player_basic?.current_club_name || dashboard?.player_basic?.equipo_transfermarkt || t('playerDetail.notAvailable')}</span>
                {dashboard?.player_basic?.edad_transfermarkt !== undefined && dashboard.player_basic.edad_transfermarkt !== null && (
                  <>
                    <span>•</span>
                    <span>
                      {dashboard.player_basic.edad_transfermarkt} {t('playerDetail.years')}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Executive Summary - KPIs */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Overall Score Card */}
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">{t('playerDetail.globalScore')}</h3>
              <Trophy className="w-6 h-6 text-yellow-400" />
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-white mb-2">
                {Math.round(playerScore?.overall_score ?? 0)}
              </div>
              <div className="text-sm text-gray-400">{t('playerDetail.outOf100')}</div>
              <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                <div className="bg-white/10 rounded-lg p-2">
                  <div className="text-gray-400">{t('playerDetail.performance')}</div>
                  <div className="text-white font-semibold">{Math.round(playerScore?.performance_score ?? 0)}</div>
                </div>
                <div className="bg-white/10 rounded-lg p-2">
                  <div className="text-gray-400">{t('playerDetail.value')}</div>
                  <div className="text-white font-semibold">{Math.round(playerScore?.value_score ?? 0)}</div>
                </div>
                <div className="bg-white/10 rounded-lg p-2">
                  <div className="text-gray-400">{t('playerDetail.potential')}</div>
                  <div className="text-white font-semibold">{Math.round(playerScore?.age_potential_score ?? 0)}</div>
                </div>
                <div className="bg-white/10 rounded-lg p-2">
                  <div className="text-gray-400">{t('playerDetail.availability')}</div>
                  <div className="text-white font-semibold">{Math.round(playerScore?.availability_score ?? 0)}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Recommendation Card */}
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">{t('playerDetail.recommendation')}</h3>
              {getRecommendationIcon(roiAnalysis?.recommendation)}
            </div>
            <div className="text-center">
              <div
                className={`w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center ${getRecommendationColor(
                  roiAnalysis?.recommendation
                )}`}
              >
                <span className="text-white text-2xl font-bold">
                  {roiAnalysis?.recommendation === 'green' ? '✓' : roiAnalysis?.recommendation === 'yellow' ? '!' : '✕'}
                </span>
              </div>
              <div className="text-white font-semibold mb-2">
                {roiAnalysis?.recommendation === 'green'
                  ? t('playerDetail.recommended')
                  : roiAnalysis?.recommendation === 'yellow'
                  ? t('playerDetail.consider')
                  : t('playerDetail.notRecommended')}
              </div>
              <div className="text-sm text-gray-400">
                {t('playerDetail.roi')}: {String((roiAnalysis?.roi_category || '').toString()).toUpperCase() || '-'}
              </div>
              {roiAnalysis?.is_undervalued && (
                <div className="mt-3 bg-green-500/20 border border-green-500/50 rounded-lg p-2 text-green-400 text-xs">
                  {t('playerDetail.undervaluedPlayer')}
                </div>
              )}
            </div>
          </div>

          {/* Contract Opportunity */}
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">{t('playerDetail.contractStatus')}</h3>
              <Calendar className="w-6 h-6 text-blue-400" />
            </div>
            <div className="space-y-3">
              <div>
                <div className="text-sm text-gray-400 mb-1">{t('playerDetail.expires')}</div>
                <div className="text-white font-semibold">
                  {contractSituation?.expires ? new Date(contractSituation.expires).toLocaleDateString(i18n.language || 'es-ES') : t('playerDetail.notAvailable')}
                </div>
              </div>
              {contractSituation?.days_remaining !== undefined && contractSituation?.days_remaining !== null && (
                <div>
                  <div className="text-sm text-gray-400 mb-1">{t('playerDetail.daysRemaining')}</div>
                  <div className="text-white font-semibold">{contractSituation.days_remaining}</div>
                </div>
              )}
              <div
                className={`p-3 rounded-lg ${
                  contractSituation?.opportunity_level === 'high'
                    ? 'bg-green-500/20 border border-green-500/50'
                    : contractSituation?.opportunity_level === 'medium'
                    ? 'bg-yellow-500/20 border border-yellow-500/50'
                    : 'bg-gray-500/20 border border-gray-500/50'
                }`}
              >
                <div className="text-xs text-gray-300 mb-1">{t('playerDetail.signingOpportunity')}</div>
                <div
                  className={`font-semibold ${
                    contractSituation?.opportunity_level === 'high'
                      ? 'text-green-400'
                      : contractSituation?.opportunity_level === 'medium'
                      ? 'text-yellow-400'
                      : 'text-gray-400'
                  }`}
                >
                  {contractSituation?.opportunity_level === 'high'
                    ? t('playerDetail.high')
                    : contractSituation?.opportunity_level === 'medium'
                    ? t('playerDetail.medium')
                    : t('playerDetail.low')}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Market Value Analysis */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Current Value & Prediction */}
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-white">{t('playerDetail.marketValueAnalysis')}</h3>
              <DollarSign className="w-6 h-6 text-green-400" />
            </div>

            <div className="space-y-4">
              <div>
                <div className="text-sm text-gray-400 mb-1">{t('playerDetail.currentValue')}</div>
                <div className="text-3xl font-bold text-white">{formatCurrency(dashboard?.player_basic?.valor_mercado ?? 0)}</div>
              </div>

              {marketValuePrediction?.prediction !== undefined && marketValuePrediction?.prediction !== null && (
                <>
                  <div className="border-t border-white/10 pt-4">
                    <div className="text-sm text-gray-400 mb-2">
                      {t('playerDetail.predictedValue')} (6 {t('dashboard.months')})
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="text-2xl font-bold text-white">{formatCurrency(marketValuePrediction.prediction)}</div>
                      <div
                        className={`flex items-center space-x-1 ${
                          marketValuePrediction?.trend === 'rising'
                            ? 'text-green-400'
                            : marketValuePrediction?.trend === 'falling'
                            ? 'text-red-400'
                            : 'text-gray-400'
                        }`}
                      >
                        {marketValuePrediction?.trend === 'rising' ? <TrendingUp className="w-5 h-5" /> : marketValuePrediction?.trend === 'falling' ? <TrendingDown className="w-5 h-5" /> : null}
                        <span className="font-semibold">
                          {((marketValuePrediction?.change_percentage ?? 0) > 0 ? '+' : '') + (marketValuePrediction?.change_percentage ?? 0).toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                    <div className="text-xs text-gray-400 mb-1">{t('playerDetail.predictionConfidence')}</div>
                    <div className="flex items-center space-x-2">
                      <div className="flex-1 bg-white/10 rounded-full h-2">
                        <div
                          className="bg-blue-500 h-2 rounded-full transition-all"
                          style={{ width: `${Math.max(0, Math.min(100, marketValuePrediction?.confidence ?? 0))}%` }}
                        />
                      </div>
                      <span className="text-white font-semibold text-sm">{(marketValuePrediction?.confidence ?? 0).toFixed(1)}%</span>
                    </div>
                  </div>
                </>
              )}

              <div className="border-t border-white/10 pt-4">
                <div className="text-sm text-gray-400 mb-2">{t('playerDetail.trend')}</div>
                <div
                  className={`inline-flex items-center space-x-2 px-3 py-1 rounded-full ${
                    marketValuePrediction?.trend === 'rising'
                      ? 'bg-green-500/20 text-green-400'
                      : marketValuePrediction?.trend === 'falling'
                      ? 'bg-red-500/20 text-red-400'
                      : marketValuePrediction?.trend === 'stable'
                      ? 'bg-blue-500/20 text-blue-400'
                      : 'bg-gray-500/20 text-gray-400'
                  }`}
                >
                  {marketValuePrediction?.trend === 'rising' ? (
                    <>
                      <TrendingUp className="w-4 h-4" />
                      <span className="font-semibold">{t('playerDetail.rise')}</span>
                    </>
                  ) : marketValuePrediction?.trend === 'falling' ? (
                    <>
                      <TrendingDown className="w-4 h-4" />
                      <span className="font-semibold">{t('playerDetail.fall')}</span>
                    </>
                  ) : marketValuePrediction?.trend === 'stable' ? (
                    <>
                      <Activity className="w-4 h-4" />
                      <span className="font-semibold">{t('playerDetail.stable')}</span>
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="w-4 h-4" />
                      <span className="font-semibold text-xs">{t('playerDetail.insufficientData')}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ROI & Performance Index */}
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-white">
                {t('playerDetail.roiPrefix')} {t('playerDetail.value')}
              </h3>
              <BarChart3 className="w-6 h-6 text-purple-400" />
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/10 rounded-lg p-4">
                  <div className="text-xs text-gray-400 mb-1">{t('playerDetail.performanceIndex')}</div>
                  <div className="text-2xl font-bold text-white">{roiAnalysis?.performance_index ?? '-'}</div>
                </div>
                <div className="bg-white/10 rounded-lg p-4">
                  <div className="text-xs text-gray-400 mb-1">{t('playerDetail.ageFactor')}</div>
                  <div className="text-2xl font-bold text-white">{roiAnalysis?.age_factor ?? '-'}x</div>
                </div>
              </div>

              <div className="border-t border-white/10 pt-4">
                <div className="text-sm text-gray-400 mb-2">{t('playerDetail.expectedGrowth')}</div>
                <div className="text-3xl font-bold text-green-400">+{roiAnalysis?.expected_growth_percentage ?? 0}%</div>
                <div className="text-xs text-gray-400 mt-1">{t('playerDetail.next12months')}</div>
              </div>

              <div
                className={`p-4 rounded-lg ${
                  roiAnalysis?.roi_category === 'high'
                    ? 'bg-green-500/20 border-2 border-green-500/50'
                    : roiAnalysis?.roi_category === 'medium'
                    ? 'bg-yellow-500/20 border-2 border-yellow-500/50'
                    : 'bg-red-500/20 border-2 border-red-500/50'
                }`}
              >
                <div className="text-xs text-gray-300 mb-2">{t('playerDetail.roiCategory')}</div>
                <div
                  className={`text-xl font-bold ${
                    roiAnalysis?.roi_category === 'high' ? 'text-green-400' : roiAnalysis?.roi_category === 'medium' ? 'text-yellow-400' : 'text-red-400'
                  }`}
                >
                  {roiAnalysis?.roi_category === 'high' ? t('playerDetail.high') : roiAnalysis?.roi_category === 'medium' ? t('playerDetail.medium') : t('playerDetail.low')}
                </div>
                <p className="text-xs text-gray-300 mt-2">
                  {roiAnalysis?.roi_category === 'high'
                    ? t('playerDetail.roiHighText')
                    : roiAnalysis?.roi_category === 'medium'
                    ? t('playerDetail.roiMediumText')
                    : t('playerDetail.roiLowText')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Injury Risk Assessment */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-white">{t('playerDetail.injuryRiskAssessment')}</h3>
              <Heart className="w-6 h-6 text-red-400" />
            </div>

            <div className="space-y-4">
              <div
                className={`p-4 rounded-lg text-center ${
                  injuryRisk?.risk_level === 'low'
                    ? 'bg-green-500/20 border border-green-500/50'
                    : injuryRisk?.risk_level === 'medium'
                    ? 'bg-yellow-500/20 border border-yellow-500/50'
                    : 'bg-red-500/20 border border-red-500/50'
                }`}
              >
                <div className="text-sm text-gray-300 mb-2">{t('playerDetail.riskLevel')}</div>
                <div
                  className={`text-3xl font-bold ${
                    injuryRisk?.risk_level === 'low' ? 'text-green-400' : injuryRisk?.risk_level === 'medium' ? 'text-yellow-400' : 'text-red-400'
                  }`}
                >
                  {injuryRisk?.risk_level === 'low' ? t('playerDetail.low') : injuryRisk?.risk_level === 'medium' ? t('playerDetail.medium') : t('playerDetail.high')}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="bg-white/10 rounded-lg p-3 text-center">
                  <div className="text-xs text-gray-400 mb-1">{t('playerDetail.injuries')}</div>
                  <div className="text-xl font-bold text-white">{injuryRisk?.total_injuries ?? 0}</div>
                </div>
                <div className="bg-white/10 rounded-lg p-3 text-center">
                  <div className="text-xs text-gray-400 mb-1">{t('playerDetail.daysLost')}</div>
                  <div className="text-xl font-bold text-white">{injuryRisk?.total_days_missed ?? 0}</div>
                </div>
                <div className="bg-white/10 rounded-lg p-3 text-center">
                  <div className="text-xs text-gray-400 mb-1">{t('playerDetail.average')}</div>
                  <div className="text-xl font-bold text-white">{injuryRisk?.avg_days_per_injury ?? 0}</div>
                </div>
              </div>

              {injuryHistory.length > 0 && (
                <div className="border-t border-white/10 pt-4">
                  <div className="text-sm text-gray-400 mb-3">{t('playerDetail.recentInjuries')}</div>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {injuryHistory.slice(0, 5).map((injury, idx) => (
                      <div key={idx} className="bg-white/5 rounded-lg p-2 text-xs">
                        <div className="flex items-center justify-between">
                          <span className="text-white font-semibold">{injury?.injury_reason || '-'}</span>
                          <span className="text-red-400">
                            {injury?.days_missed ?? 0} {t('playerDetail.days')}
                          </span>
                        </div>
                        <div className="text-gray-400 mt-1">{injury?.season_name || '-'}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* National Team Experience */}
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-white">{t('playerDetail.internationalExperience')}</h3>
              <Shield className="w-6 h-6 text-blue-400" />
            </div>

            {nationalTeam.length > 0 ? (
              <div className="space-y-4">
                {nationalTeam.map((nt, idx) => (
                  <div key={idx} className="bg-white/10 rounded-lg p-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs text-gray-400 mb-1">{t('playerDetail.matches')}</div>
                        <div className="text-2xl font-bold text-white">{nt?.matches ?? 0}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-400 mb-1">{t('playerDetail.goals')}</div>
                        <div className="text-2xl font-bold text-white">{nt?.goals ?? 0}</div>
                      </div>
                    </div>
                    {nt?.debut && (
                      <div className="mt-3 text-xs text-gray-400">
                        {t('playerDetail.debut')}: {new Date(nt.debut).toLocaleDateString(i18n.language || 'es-ES')}
                      </div>
                    )}
                    {nt?.career_state && (
                      <div className="mt-1">
                        <span
                          className={`inline-block px-2 py-1 rounded text-xs font-semibold ${
                            nt.career_state === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                          }`}
                        >
                          {t(`playerDetail.careerState.${nt.career_state}`) || nt.career_state}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400">
                <Shield className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>{t('playerDetail.noInternationalExperience')}</p>
              </div>
            )}
          </div>
        </div>

        {/* Performance History Summary */}
        {performances.length > 0 && (
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 mb-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-white">{t('playerDetail.performanceSummary')}</h3>
              <Activity className="w-6 h-6 text-purple-400" />
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="text-left text-xs text-gray-400 uppercase">
                  <tr>
                    <th className="pb-3">{t('playerDetail.season')}</th>
                    <th className="pb-3">{t('playerDetail.competition')}</th>
                    <th className="pb-3 text-center">{t('playerDetail.matchesPlayed')}</th>
                    <th className="pb-3 text-center">{t('playerDetail.goals')}</th>
                    <th className="pb-3 text-center">{t('playerDetail.assists')}</th>
                    <th className="pb-3 text-center">{t('playerDetail.minutes')}</th>
                  </tr>
                </thead>
                <tbody className="text-white text-sm">
                  {performances.slice(0, 5).map((perf, idx) => (
                    <tr key={idx} className="border-t border-white/10">
                      <td className="py-3 font-semibold">{perf?.season_name ?? '-'}</td>
                      <td className="py-3 text-gray-300">{perf?.competition_name ?? '-'}</td>
                      <td className="py-3 text-center">{perf?.nb_on_pitch ?? 0}</td>
                      <td className="py-3 text-center text-green-400 font-semibold">{perf?.goals ?? 0}</td>
                      <td className="py-3 text-center text-blue-400 font-semibold">{perf?.assists ?? 0}</td>
                      <td className="py-3 text-center text-gray-300">{perf?.minutes_played ?? 0}'</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Transfer History */}
        {transferHistory.length > 0 && (
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-white">{t('playerDetail.transferHistory')}</h3>
              <Users className="w-6 h-6 text-green-400" />
            </div>

            <div className="space-y-3">
              {transferHistory.map((transfer, idx) => (
                <div key={idx} className="bg-white/10 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-white font-semibold">{transfer?.season_name ?? '-'}</div>
                    <div className="text-xs text-gray-400">{transfer?.transfer_type ?? '-'}</div>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-gray-300">
                    <span>{transfer?.from_team_name ?? '-'}</span>
                    <span>→</span>
                    <span>{transfer?.to_team_name ?? '-'}</span>
                  </div>
                  {transfer?.transfer_fee && transfer.transfer_fee > 0 && (
                    <div className="mt-2 text-green-400 font-semibold">{formatCurrency(transfer.transfer_fee)}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlayerIntelligenceDashboard;
